<script setup lang="ts">
/**
 * Module: web/src/components/admin/PinIssueModal.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Quick-PIN issuance dialog (doc 04 sect 6.6 + 9.6):
 *   1. pick a PERSON -- search across account-holders AND accountless people,
 *      with a "New person" quick-create (name + dispatcher/driver flags)
 *   2. choose scope from the LOW-RISK allow-list ONLY (calendar / data_entry
 *      -- the UI offers nothing else, belt-and-suspenders over the DB CHECK)
 *   3. optional expiry
 *   4. server generates the 4 digits -> shown here ONCE, never re-displayable
 */
import { computed, ref, watch } from 'vue';
import { apiGet, apiPost, unwrapList, ApiError } from '@/composables/useApi';
import { PIN_SCOPE_ALLOWLIST, type AdminPerson, type PinIssueResponse } from '@/types/api';
import BaseModal from '@/components/BaseModal.vue';
import AppIcon from '@/components/AppIcon.vue';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'issued'): void;
}>();

const people = ref<AdminPerson[]>([]);
const search = ref('');
const selectedPersonId = ref<number | null>(null);
const scope = ref<string[]>(['data_entry']);
const expiresAt = ref(''); // datetime-local value; '' = no expiry
const busy = ref(false);
const errorMessage = ref('');

// New-person quick-create state
const creatingPerson = ref(false);
const newPersonName = ref('');
const newPersonDispatcher = ref(false);
const newPersonDriver = ref(true);

// Show-once result
const issuedPin = ref<string | null>(null);
const issuedPersonName = ref('');

const filteredPeople = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return people.value;
  return people.value.filter((p) => p.name.toLowerCase().includes(q));
});

const selectedPerson = computed(() => people.value.find((p) => p.id === selectedPersonId.value) ?? null);

const SCOPE_LABELS: Record<string, string> = {
  data_entry: 'MO data entry',
  calendar: 'Calendar (v2 — surface pending)',
};

async function loadPeople() {
  try {
    const body = await apiGet<unknown>('/admin/people');
    people.value = unwrapList<AdminPerson>(body, 'people');
  } catch {
    errorMessage.value = 'Could not load people.';
  }
}

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    errorMessage.value = '';
    search.value = '';
    selectedPersonId.value = null;
    scope.value = ['data_entry'];
    expiresAt.value = '';
    creatingPerson.value = false;
    newPersonName.value = '';
    newPersonDispatcher.value = false;
    newPersonDriver.value = true;
    issuedPin.value = null;
    issuedPersonName.value = '';
    void loadPeople();
  },
  { immediate: true },
);

function toggleScope(cap: string) {
  scope.value = scope.value.includes(cap)
    ? scope.value.filter((s) => s !== cap)
    : [...scope.value, cap];
}

async function createPerson() {
  if (busy.value) return;
  errorMessage.value = '';
  if (!newPersonName.value.trim()) {
    errorMessage.value = 'Enter the new person\'s name.';
    return;
  }
  busy.value = true;
  try {
    const created = await apiPost<Record<string, unknown>>('/admin/people', {
      name: newPersonName.value.trim(),
      is_dispatcher: newPersonDispatcher.value,
      is_driver: newPersonDriver.value,
    });
    const personObj = (created?.person ?? created ?? {}) as Record<string, unknown>;
    const id = typeof personObj.id === 'number' ? personObj.id : null;
    await loadPeople();
    if (id !== null) selectedPersonId.value = id;
    creatingPerson.value = false;
    newPersonName.value = '';
  } catch (e) {
    errorMessage.value = e instanceof ApiError ? (e.detail ?? 'Could not create the person.') : 'Could not create the person.';
  } finally {
    busy.value = false;
  }
}

async function issue() {
  if (busy.value) return;
  errorMessage.value = '';
  if (selectedPersonId.value === null) {
    errorMessage.value = 'Pick a person first.';
    return;
  }
  if (scope.value.length === 0) {
    errorMessage.value = 'Choose at least one scope.';
    return;
  }
  busy.value = true;
  try {
    const resp = await apiPost<PinIssueResponse>('/admin/pins', {
      person_id: selectedPersonId.value,
      scope: scope.value,
      ...(expiresAt.value ? { expires_at: new Date(expiresAt.value).toISOString() } : {}),
    });
    issuedPin.value = resp?.pin ?? null;
    issuedPersonName.value = selectedPerson.value?.name ?? '';
    emit('issued');
  } catch (e) {
    errorMessage.value = e instanceof ApiError ? (e.detail ?? 'PIN issuance failed.') : 'PIN issuance failed.';
  } finally {
    busy.value = false;
  }
}

function done() {
  issuedPin.value = null;
  emit('close');
}
</script>

<template>
  <BaseModal
    :open="open"
    :title="issuedPin ? 'PIN issued' : 'Issue Quick PIN'"
    max-width="md:max-w-xl"
    @close="issuedPin ? done() : emit('close')"
  >
    <!-- Show-PIN-once -->
    <div v-if="issuedPin" class="text-center py-4">
      <p class="text-sm text-fg-muted mb-4">
        Quick-access PIN for <span class="font-semibold text-fg">{{ issuedPersonName }}</span>.
        Hand it over now — it is shown <span class="font-semibold text-accent-warm">once</span> and can never
        be re-displayed (revoke + reissue if lost).
      </p>
      <div class="inline-flex items-center gap-3 bg-input border border-border-c rounded-2xl px-8 py-6">
        <span
          v-for="(d, i) in issuedPin.split('')"
          :key="i"
          class="text-4xl font-bold text-fg tracking-widest"
        >{{ d }}</span>
      </div>
      <p class="mt-4 text-xs text-fg-muted">
        Scope: <code>{{ scope.join(', ') }}</code>
      </p>
    </div>

    <!-- Issue form -->
    <div v-else class="space-y-5">
      <div v-if="errorMessage" class="alert-error" role="alert">{{ errorMessage }}</div>

      <!-- 1. Person -->
      <div>
        <div class="flex items-center justify-between mb-1.5">
          <label class="field-label !mb-0" for="pin-search">Person</label>
          <button
            type="button"
            class="text-sm text-accent hover:underline py-1"
            @click="creatingPerson = !creatingPerson"
          >
            {{ creatingPerson ? 'Back to search' : '+ New person' }}
          </button>
        </div>

        <div v-if="creatingPerson" class="space-y-3 border border-border-c rounded-lg p-3">
          <input
            v-model="newPersonName"
            class="field-input"
            type="text"
            placeholder="Full name"
            autocomplete="off"
          />
          <div class="flex gap-4">
            <label class="flex items-center gap-2 text-sm text-fg min-h-[44px] cursor-pointer">
              <input v-model="newPersonDispatcher" type="checkbox" class="w-5 h-5 accent-[var(--accent)]" /> Dispatcher
            </label>
            <label class="flex items-center gap-2 text-sm text-fg min-h-[44px] cursor-pointer">
              <input v-model="newPersonDriver" type="checkbox" class="w-5 h-5 accent-[var(--accent)]" /> Driver
            </label>
          </div>
          <p class="text-xs text-fg-muted">
            Creates a staff-identity row only — no login account. The PIN attaches to the person.
          </p>
          <button type="button" class="btn-secondary w-full" :disabled="busy" @click="createPerson">
            {{ busy ? 'Creating…' : 'Create person' }}
          </button>
        </div>

        <template v-else>
          <input
            id="pin-search"
            v-model="search"
            class="field-input mb-2"
            type="text"
            placeholder="Search people (account-holders and accountless)"
            autocomplete="off"
          />
          <div class="max-h-44 overflow-y-auto border border-border-c rounded-lg divide-y divide-[var(--border)]">
            <button
              v-for="p in filteredPeople"
              :key="p.id"
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-3 text-left text-sm min-h-[48px] transition-colors"
              :class="selectedPersonId === p.id ? 'bg-accent/10 text-fg' : 'text-fg-muted hover:bg-input'"
              @click="selectedPersonId = p.id"
            >
              <span class="truncate">{{ p.name }}</span>
              <AppIcon v-if="selectedPersonId === p.id" name="check" class="w-4 h-4 text-accent flex-shrink-0" />
            </button>
            <div v-if="filteredPeople.length === 0" class="px-3 py-4 text-sm text-fg-muted text-center">
              No match — use “New person” above.
            </div>
          </div>
        </template>
      </div>

      <!-- 2. Scope (low-risk allow-list ONLY) -->
      <div>
        <label class="field-label">Scope — low-risk features only</label>
        <div class="space-y-2">
          <label
            v-for="cap in PIN_SCOPE_ALLOWLIST"
            :key="cap"
            class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer min-h-[48px] transition-colors"
            :class="scope.includes(cap) ? 'border-accent bg-accent/5' : 'border-border-c hover:bg-input'"
          >
            <input
              type="checkbox"
              class="w-5 h-5 accent-[var(--accent)]"
              :checked="scope.includes(cap)"
              @change="toggleScope(cap)"
            />
            <span class="text-sm text-fg">{{ SCOPE_LABELS[cap] ?? cap }}</span>
          </label>
        </div>
        <p class="mt-1.5 text-xs text-fg-muted">
          A PIN can never carry manager, admin, reporting, or recordings access.
        </p>
      </div>

      <!-- 3. Expiry -->
      <div>
        <label class="field-label" for="pin-expiry">Expires (optional)</label>
        <input id="pin-expiry" v-model="expiresAt" class="field-input" type="datetime-local" />
      </div>
    </div>

    <template #footer>
      <div class="flex gap-3 justify-end">
        <template v-if="issuedPin">
          <button type="button" class="btn-primary" @click="done">Done — PIN handed over</button>
        </template>
        <template v-else>
          <button type="button" class="btn-secondary" :disabled="busy" @click="emit('close')">Cancel</button>
          <button type="button" class="btn-primary" :disabled="busy || creatingPerson" @click="issue">
            {{ busy ? 'Issuing…' : 'Generate PIN' }}
          </button>
        </template>
      </div>
    </template>
  </BaseModal>
</template>
